import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Alert, Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { capitalize } from 'lodash'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import Header from 'components/Header'
import Confirm from 'components/Dialog'
import { DescriptionIcon, EditIcon } from 'components/Icons'
import { useSession } from 'components/Providers'
import { changeStatus } from 'store/needs-attention/address-change'
import { runWidget } from 'store/widget'
import { openFile } from 'utils/file'
import formatAddress from 'utils/formatAddress'
import { MIN_PROOF_FILES, visibleProofList } from 'utils/proofOfAddress'
import { ADDRESS_CHANGE_STATUS_COLORS } from 'constants'
import { fullName } from './List'
import * as snackbar from 'utils/snackbar'
import { ArrowDownward } from '@mui/icons-material'

const ACTIONS = {
  approve: {
    title: 'Approve Address Change',
    message: 'Are you sure you want to approve this address change?',
    done: 'Address change approved',
  },
  reject: {
    title: 'Reject Address Change',
    message: 'Are you sure you want to reject this address change? The parent will have to submit a new request.',
    done: 'Address change rejected',
  },
}

const Card = ({ title, onEdit, children }) => {
  const { t } = useTranslation()

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" sx={{ px: 2, py: 0.25, bgcolor: 'action.hover', minHeight: 36 }}>
        <Typography variant="overline" color="text.secondary" sx={{ flex: 1, lineHeight: 2 }}>
          {title}
        </Typography>
        {onEdit && (
          <Tooltip title={t('Edit')}>
            <IconButton size="small" onClick={onEdit} aria-label={`${t('Edit')} ${title}`}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Box sx={{ px: 2, py: 1.5 }}>{children}</Box>
    </Paper>
  )
}

export default function Review({ request, onClose, onEditSection }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { person } = useSession()
  const [action, setAction] = useState(null)
  const [busy, setBusy] = useState(false)

  const isPending = request.status === 'pending'
  const editable = isPending
  const edit = section => (editable ? () => onEditSection(section) : undefined)

  const files = visibleProofList(request.proofOfAddress)
  const approveBlocked =
    files.length < MIN_PROOF_FILES
      ? t('At least {{count}} proof of address files are required.', { count: MIN_PROOF_FILES })
      : null

  const previousState = request.primaryParentAddresses?.find(address => address?.city)?.city
  const newState = request.address?.city
  const stateChanged =
    isPending &&
    Boolean(previousState && newState) &&
    previousState.trim().toLowerCase() !== newState.trim().toLowerCase()

  const people = [
    { ...request.primaryParent, role: t('Requesting parent') },
    ...(request.secondaryParents ?? []).map(parent => ({ ...parent, role: t('Other parent') })),
    ...(request.students ?? []).map(student => ({ ...student, role: t('Student') })),
  ]

  const onConfirm = async () => {
    const current = action
    setAction(null)
    setBusy(true)
    const res = await dispatch(changeStatus({ type: 'student', id: request.id, action: current }))
    setBusy(false)
    if (res.error) return

    snackbar.success(t(ACTIONS[current].done))
    dispatch(runWidget({ type: person?.type, code: 'needsAttention' }))
    onClose()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header
        title={fullName(request.primaryParent)}
        small
        close={onClose}
        borderBottom
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          <Chip label={t(capitalize(request.status))} color={ADDRESS_CHANGE_STATUS_COLORS[request.status]} size="small" />
          <Typography variant="body2" color="text.secondary">
            {t('Address change')} · {t('Submitted')} {dayjs(request.createdAt).format('MM/DD/YYYY')}
          </Typography>
        </Stack>
      </Header>

      <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 2 }}>
        <Card title={t('Address')} onEdit={edit('address')}>
          {stateChanged && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {t('The city changed from {{previous}} to {{next}}. A transfer card is required.', {
                previous: previousState,
                next: newState,
              })}
            </Alert>
          )}
          {request?.primaryParentAddresses?.length ? (
            request.primaryParentAddresses.map((address, index) => (
              <>
                <Typography variant="body2" color="text.secondary">{t('Previous Address')}</Typography>
                <Typography key={index} variant="body1" >
                  {formatAddress(address)}
                </Typography>
              </>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('No previous address on file')}
            </Typography>
          )}
          <ArrowDownward color="action" fontSize="small" />
          <>
          <Typography variant="body2" color="text.secondary">{t('New Address')}</Typography>          
          <Typography variant="body1" fontWeight={600}>
            {formatAddress(request.address) || '--'}
          </Typography>
          </>
        </Card>

        <Card title={`${t('People affected')} (${people.length})`} onEdit={request.secondaryParents?.length ? edit('people') : undefined}>
          <Stack spacing={1.25}>
            {people.map(item => (
              <Stack key={`${item.role}-${item.id}`} direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {fullName(item)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.role}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Card>

        <Card title={`${t('Proof of Address')} (${files.length})`} onEdit={edit('files')}>
          {files.length ? (
            <Stack spacing={1}>
              {files.map((file, index) => (
                <Stack key={file.fileId ?? index} direction="row" spacing={1.5} alignItems="center">
                  <DescriptionIcon color="primary" fontSize="small" />
                  <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }} title={file.name}>
                    {file.name || `${t('File')} ${index + 1}`}
                  </Typography>
                  {file.fileUuid ? (
                    <Button size="small" onClick={() => openFile(file.fileUuid)}>
                      {t('View')}
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      {t('Not viewable')}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.disabled">
              {t('No files')}
            </Typography>
          )}
        </Card>
      </Stack>

      {isPending && (
        <>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 3, py: 2 }}>
            <Typography variant="caption" color={approveBlocked ? 'warning.main' : 'text.secondary'} sx={{ flex: 1 }}>
              {approveBlocked}
            </Typography>
            <Button variant="outlined" color="error" disabled={busy} onClick={() => setAction('reject')}>
              {t('Reject')}
            </Button>
            <Button
              variant="contained"
              disabled={busy || Boolean(approveBlocked)}
              onClick={() => setAction('approve')}
            >
              {t('Approve')}
            </Button>
          </Stack>
        </>
      )}

      <Confirm
        open={Boolean(action)}
        onClose={() => setAction(null)}
        onConfirm={onConfirm}
        dialogTitle={action && t(ACTIONS[action].title)}
        message={action && t(ACTIONS[action].message)}
      />
    </Box>
  )
}
